/**
 * Resolve "do I already have a local deck for this SG deck?"
 *
 * The personal store can hold up to two rows pointing at the same SG
 * deck:
 *   1. The owner's original personal deck — has `linkedGroups` set by
 *      `sharing.share()`, but its `deckPaths` are the original deck
 *      name (NOT the `__sg_{groupId}_{deckName}` form).
 *   2. A `__sg_*` mirror row created by `sg-deck-view.saveToDexie` or
 *      `seedMirrorIfMissing` for users who don't already have the
 *      original deck (typically members, but historically the owner
 *      too thanks to the deckPaths-only lookup).
 *
 * Two-pass priority:
 *   Pass 1 — find any deck whose `linkedGroups` has an entry matching
 *            (groupId, sgDeckId). Catches the owner's original deck
 *            and any properly-linked member mirror. Tombstoned decks
 *            (`deletedTimeISO` past `addedTimeISO`) are skipped — a
 *            stale link on a deleted deck must NOT block a fresh
 *            mirror creation.
 *   Pass 2 — fallback: any deck whose `deckPaths` includes the legacy
 *            `__sg_*` path. Catches member mirrors created before the
 *            sharing.ts `withServerSource` fix landed.
 *
 * Returns `null` when neither matches — caller mints a fresh mirror.
 *
 * This helper is the SINGLE source of truth for the "is this SG deck
 * already mirrored locally?" question. Three call sites use it:
 *   - `sg-deck-view.tsx:mirrorDeckId` (decides where SG-view writes go)
 *   - `sg-deck-view.tsx:saveToDexie` (decides reuse vs. mint)
 *   - `seedMirrorIfMissing` in `use-load-sg-folder-decks.ts` (folder
 *     listing pre-seed)
 *
 * Keeping the predicate in one place stops the three call sites from
 * drifting out of sync — they did exactly that before this helper was
 * extracted, which is what spawned the duplicate-mirror bug.
 */

import type { DeckProperties } from '../provider/sync-storage';

export function findLocalDeckIdForSGDeck(
  allProps: Record<string, DeckProperties>,
  groupId: string,
  sgDeckId: string,
  sgKey: string,
): string | null {
  // Pass 1 — linkedGroups match (canonical case).
  for (const [id, props] of Object.entries(allProps)) {
    if (isTombstoned(props)) continue;
    const linked = props.linkedGroups?.some(
      (l) => l.groupId === groupId && l.sgDeckId === sgDeckId,
    );
    if (linked) return id;
  }
  // Pass 2 — deckPaths fallback (legacy mirrors).
  for (const [id, props] of Object.entries(allProps)) {
    if (isTombstoned(props)) continue;
    if (props.deckPaths?.includes(sgKey as DeckProperties['deckPaths'][number])) {
      return id;
    }
  }
  return null;
}

/**
 * EVERY live local deck that stands for the SG deck — the owner's original,
 * linked mirrors and legacy `__sg_*` mirrors — in the resolver's priority
 * order. `findLocalDeckIdForSGDeck` picks one to show; an operation on the
 * viewer's own progress for the deck (a progress reset) must reach all of
 * them, or a twin that is not the one on screen keeps the old progress.
 */
export function findAllLocalDeckIdsForSGDeck(
  allProps: Record<string, DeckProperties>,
  groupId: string,
  sgDeckId: string,
  sgKey: string | undefined,
): string[] {
  const out: string[] = [];
  for (const [id, props] of Object.entries(allProps)) {
    if (isTombstoned(props)) continue;
    if (
      props.linkedGroups?.some((l) => l.groupId === groupId && l.sgDeckId === sgDeckId)
    ) {
      out.push(id);
    }
  }
  if (sgKey) {
    for (const [id, props] of Object.entries(allProps)) {
      if (isTombstoned(props) || out.includes(id)) continue;
      if (props.deckPaths?.includes(sgKey as DeckProperties['deckPaths'][number]))
        out.push(id);
    }
  }
  return out;
}

/** A deck is "tombstoned" if it was soft-deleted and not re-added since. */
export function isTombstoned(props: DeckProperties): boolean {
  if (!props.deletedTimeISO) return false;
  if (!props.addedTimeISO) return true;
  return props.addedTimeISO <= props.deletedTimeISO;
}
